// Picker de fondos para el modo "Imagen con modelo" de Interfaz V2.
//
// Espejea a ModelPicker en estructura: 3 sub-pantallas controladas por
// un mini state machine (`view`):
//
//   1. 'list'     — carrusel con tile "Automático" sticky + fondos de
//                   biblioteca + fondos guardados del user. Debajo dos
//                   botones: "Describí el fondo" + "Cargá tu propio fondo".
//   2. 'describe' — vista con un textarea estilo chat input + botón
//                   "Mejorar" (no-op por ahora) + Confirmar.
//   3. 'upload'   — file picker + preview + Confirmar (sube a Supabase
//                   y queda en la biblioteca del user para reuso).
//
// Variants: Sheet (mobile bottom sheet) e Inline (desktop, embedded en
// el ConfigurePanel).

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowLeft,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  CloudSun,
  Sparkles,
  Sun,
  Type as TypeIcon,
  Upload,
  UserPlus,
  X,
} from 'lucide-react';
import {
  uploadFondo,
  type FondoBiblioteca,
  type FondoGuardado,
} from './fondosApi';
import { AnimatedBlurBg } from './AnimatedBlurBg';
import { HoverScrollCarousel } from './HoverScrollCarousel';
import { AddOptionCard } from './components/AddOptionCard';
import { AccionCard } from './components/AccionCard';
import { normalizeImageForUpload, UnsupportedImageFormatError } from '../../utils/heicConvert';
import CachedImg from '../../components/CachedImg';
import { useExitTransition } from '../../hooks/useExitTransition';
import {
  COLORES_FONDO_LISO,
  alElegirLiso,
  etiquetaDelLiso,
  hexDelLiso,
  ILUMINACION_BLANCO_DEFAULT,
  TEMPERATURA_BLANCO_DEFAULT,
  type ColorFondoLiso,
  type IluminacionBlanco,
  type TemperaturaBlanco,
} from './coloresDeFondoLiso';
import { PromptRecientesButton } from './components/PromptRecientesButton';
import {
  useDescribeChat,
  type DescribeChatMessage,
} from './useDescribeChat';

// ─── Tipos ─────────────────────────────────────────────────────────────

/** Tono del blanco. El backend NO lo interpreta con IA: es el RGB objetivo al que
 *  normaliza el fondo ya generado (ADR-2026-08-14-fondo-blanco-consistente). */
// Viven en `coloresDeFondoLiso` para que el armado del payload los use sin importar este
// componente; se re-exportan porque varios los importan de acá.
export {
  TEMPERATURA_BLANCO_DEFAULT,
  ILUMINACION_BLANCO_DEFAULT,
} from './coloresDeFondoLiso';
import { ZonaDeSubida } from './components/ZonaDeSubida';
import {
  AsideDeAlta,
  BotonPrincipalDeAlta,
  ColumnaDeTrabajo,
  ColumnasDeAlta,
  PieDeAlta,
  TipsDeAlta,
} from './components/altaEnDosColumnas';
export type { TemperaturaBlanco, IluminacionBlanco } from './coloresDeFondoLiso';

export type FondoSelection =
  | { mode: 'auto' }
  | {
      mode: 'blanco';
      /** Ausentes ⇒ los defaults de arriba. Se guardan en la selección (y no en un
       *  state aparte) para que viajen solos en el restore de la config. */
      temperatura?: TemperaturaBlanco;
      iluminacion?: IluminacionBlanco;
      /** Color del "Fondo liso" (default 'blanco'). Mismo criterio que
       *  temperatura/iluminacion: viaja en la selección, así el restore de la
       *  config lo trae solo. Nació en Fotos de producto; desde
       *  ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo lo leen también
       *  Modelo y Catálogo. Unión inline (y no el `ColorFondoLiso` del catálogo)
       *  para no acoplar el tipo base a otro módulo; son la misma lista y
       *  `coloresDeFondoLiso.ts` es la fuente. */
      colorLiso?: 'blanco' | 'beige' | 'rosa' | 'negro';
    }
  | {
      mode: 'biblioteca';
      id: number;
      name: string;
      imageUrl: string;
    }
  | {
      mode: 'guardado';
      fileName: string;
      name: string;
      imageUrl: string;
    }
  | { mode: 'descripcion'; text: string };

export type FondoPickerView = 'list' | 'describe' | 'upload' | 'all';

/**
 * Los ajustes del fondo blanco, en una línea: "Neutro · luz difusa".
 *
 * Vive acá —al lado del control que los edita— para que la etiqueta y el control no se
 * separen. Los usa la card de Fondo del panel de Modelo v2, que muestra con qué se va a
 * generar: "Fondo blanco" a secas no dice cuál de los seis blancos quedó elegido.
 */
export const fondoBlancoAjustesLabel = (sel: FondoSelection | null): string => {
  if (!sel || sel.mode !== 'blanco') return '';
  const temperatura =
    TEMPERATURA_OPCIONES.find(
      (o) => o.id === (sel.temperatura ?? TEMPERATURA_BLANCO_DEFAULT),
    )?.label ?? '';
  const luz =
    (sel.iluminacion ?? ILUMINACION_BLANCO_DEFAULT) === 'dura'
      ? 'luz dura'
      : 'luz difusa';
  return `${temperatura} · ${luz}`;
};

export const fondoSelectionLabel = (sel: FondoSelection | null): string => {
  if (!sel) return 'Sugerido';
  if (sel.mode === 'auto') return 'Automático';
  if (sel.mode === 'blanco') return etiquetaDelLiso(sel);
  if (sel.mode === 'biblioteca') return sel.name;
  if (sel.mode === 'guardado') return sel.name;
  return 'Descripción';
};

// ─── Sub-componente: ListView ─────────────────────────────────────────

interface ListViewProps {
  selection: FondoSelection | null;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  onSelectAuto: () => void;
  onSelectBlanco: () => void;
  onSelectBiblioteca: (f: FondoBiblioteca) => void;
  onSelectGuardado: (f: FondoGuardado) => void;
  onTapDescribe: () => void;
  onTapUpload: () => void;
  /** Slot para los ajustes del fondo blanco (`AjustesFondoBlanco`), entre el carrusel
   *  y los CTAs. Solo lo usa la variante inline: en el sheet, elegir "Fondo blanco"
   *  cierra la hoja, así que los controles no llegarían a verse. */
  ajustesBlanco?: React.ReactNode;
  /** Ver `AllFondosViewProps.accionSecundaria`. En 'anadir', "Agregar fondo" va
   *  DERECHO a subir un fondo propio y desaparece la opción de describir. */
  accionSecundaria?: 'describir' | 'anadir';
  variant: 'sheet' | 'inline';
  /** Inline desktop: si está provisto, el carrusel muestra cards de
   *  CATEGORÍAS (no fondos individuales) y al tocar una se llama con el
   *  id de la categoría — abre el sheet lateral en esa categoría. */
  onOpenCategory?: (category: string) => void;
  /** Inline desktop: agrega una card "Ver más" al final del carrusel, que
   *  abre la grilla completa (mismo destino que el link del header). */
  onVerMas?: () => void;
}

const ListView: React.FC<ListViewProps> = ({
  selection,
  biblioteca,
  guardados,
  onSelectAuto,
  onSelectBlanco,
  onSelectBiblioteca,
  onSelectGuardado,
  onTapDescribe,
  onTapUpload,
  ajustesBlanco,
  accionSecundaria = 'describir',
  variant,
  onOpenCategory,
  onVerMas,
}) => {
  const isSheet = variant === 'sheet';
  const cardClass = isSheet
    ? 'shrink-0 w-[220px] aspect-[9/16]'
    : 'shrink-0 w-[140px] aspect-[9/16]';
  const autoSelected = selection?.mode === 'auto';
  const blancoSelected = selection?.mode === 'blanco';
  const bibliotecaSelectedId =
    selection?.mode === 'biblioteca' ? selection.id : null;
  const guardadoSelectedFile =
    selection?.mode === 'guardado' ? selection.fileName : null;
  const descripcionSelected = selection?.mode === 'descripcion';

  // Tiles "Automático" y "Fondo blanco" — reutilizados por el carrusel /
  // grid de fondos (sheet) y por el carrusel de categorías (inline).
  const autoTile = (cls: string, big: boolean) => (
    <button
      key="auto"
      type="button"
      onClick={onSelectAuto}
      className={`${cls} rounded-2xl overflow-hidden relative flex flex-col items-center justify-center gap-2.5 text-white shadow-sm transition-transform active:scale-[0.97] bg-brand-primary ${
        autoSelected ? 'ring-2 ring-brand-primary ring-offset-2' : ''
      }`}
    >
      <AnimatedBlurBg pauseOnDesktopUnselected selected={autoSelected} />
      <Sparkles
        className={`relative z-10 ${big ? 'w-9 h-9 drop-shadow' : 'w-7 h-7 drop-shadow'}`}
      />
      <span
        className={`relative z-10 ${big ? 'text-[15px]' : 'text-[13.5px]'} font-semibold leading-tight px-2 text-center drop-shadow`}
      >
        Automático
      </span>
    </button>
  );
  const blancoTile = (cls: string, big: boolean) => (
    <button
      key="blanco"
      type="button"
      onClick={onSelectBlanco}
      className={`${cls} rounded-2xl overflow-hidden bg-white border-2 ${
        blancoSelected ? 'border-brand-primary' : 'border-neutral-200'
      } transition-all relative active:scale-[0.97] flex flex-col items-center justify-center gap-2`}
    >
      <span
        className={`inline-flex items-center justify-center rounded-md border-2 border-neutral-300 ${big ? 'w-12 h-12' : 'w-9 h-9'}`}
        style={{ backgroundColor: hexDelLiso(selection) }}
      />
      <span
        className={`text-brand-black/85 font-semibold leading-tight ${big ? 'text-[15px]' : 'text-[13px]'}`}
      >
        {etiquetaDelLiso(selection)}
      </span>
      {blancoSelected && (
        <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
          <Check className="w-4 h-4" strokeWidth={3} />
        </span>
      )}
    </button>
  );

  // Cards de fondos. `cls` fija la medida (carrusel: fija; grid: fluida),
  // `big` el tamaño de iconos/texto. Devuelve un array para reusar en el
  // carrusel mobile y el grid desktop.
  const renderCards = (cls: string, big: boolean): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    // Automático
    out.push(autoTile(cls, big));
    // Fondos guardados del user
    for (const f of guardados) {
      const isSel = guardadoSelectedFile === f.fileName;
      out.push(
        <button
          key={`g-${f.id}`}
          type="button"
          onClick={() => onSelectGuardado(f)}
          className={`${cls} rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
            isSel ? 'border-brand-primary' : 'border-neutral-200'
          } transition-all relative active:scale-[0.97]`}
        >
          {f.imageUrl && (
            <CachedImg
              src={f.imageUrl}
              alt={f.nombre}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          )}
          {isSel && (
            <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
              <Check className="w-4 h-4" strokeWidth={3} />
            </span>
          )}
          <div
            className={`absolute inset-x-0 bottom-0 ${big ? 'px-3 py-2.5' : 'px-2 py-1.5'} bg-gradient-to-t from-black/65 via-black/15 to-transparent`}
          >
            <p
              className={`text-left text-white ${big ? 'text-[15px]' : 'text-[13px]'} font-semibold leading-tight drop-shadow truncate`}
            >
              {f.nombre}
            </p>
          </div>
        </button>,
      );
    }
    // Fondo blanco — tile estático
    out.push(blancoTile(cls, big));
    // Biblioteca pública
    for (const f of biblioteca) {
      const isSel = bibliotecaSelectedId === f.id;
      out.push(
        <button
          key={`b-${f.id}`}
          type="button"
          onClick={() => onSelectBiblioteca(f)}
          className={`${cls} rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
            isSel ? 'border-brand-primary' : 'border-neutral-200'
          } transition-all relative active:scale-[0.97]`}
        >
          {f.imageUrl && (
            <CachedImg
              src={f.imageUrl}
              alt={f.nombre}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          )}
          <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-black/55 backdrop-blur-sm text-white text-[9.5px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-md">
            <Sparkles className="w-2.5 h-2.5" />
            Atlas
          </span>
          {isSel && (
            <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
              <Check className="w-4 h-4" strokeWidth={3} />
            </span>
          )}
          <div
            className={`absolute inset-x-0 bottom-0 ${big ? 'px-3 py-2.5' : 'px-2 py-1.5'} bg-gradient-to-t from-black/65 via-black/15 to-transparent`}
          >
            <p
              className={`text-left text-white ${big ? 'text-[15px]' : 'text-[13px]'} font-semibold leading-tight drop-shadow truncate`}
            >
              {f.nombre}
            </p>
          </div>
        </button>,
      );
    }
    return out;
  };

  const soloAnadir = accionSecundaria === 'anadir';

  // Opciones de la card "Agregar fondo" (popover en el grid desktop).
  const addOptions = [
    {
      icon: <TypeIcon className="w-3.5 h-3.5" />,
      label: 'Describir tu fondo',
      onClick: onTapDescribe,
    },
    {
      icon: <UserPlus className="w-3.5 h-3.5" />,
      label: 'Cargá tu propio fondo',
      onClick: onTapUpload,
    },
  ];

  const carousel = (
    <HoverScrollCarousel
      // `py-2 -my-2`: `overflow-x-auto` fuerza `overflow-y` a `auto` (spec CSS)
      // → recortaba el `ring-offset-2` de la card "Automático" arriba/abajo. El
      // padding le da aire al ring y el margen negativo compensa el layout.
      // (Mismo fix que ModelPicker, ADR-100.)
      className="flex gap-3 overflow-x-auto -mx-5 px-5 py-2 -my-2 scrollbar-hide"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
      arrowInset={4}
    >
      {renderCards(cardClass, isSheet)}
    </HoverScrollCarousel>
  );

  // ── Carrusel de CATEGORÍAS (inline desktop) ──────────────────────────
  // En vez de fondos individuales: Automático + Fondo blanco + "Mis
  // fondos" + una card por categoría de biblioteca. Tocar una categoría
  // abre el sheet lateral directo en esa categoría (onOpenCategory).
  const categorias = React.useMemo(() => {
    const map = new Map<string, { count: number; previewUrl: string }>();
    biblioteca.forEach((f) => {
      const key = f.categoria || 'Otros';
      const entry = map.get(key);
      if (entry) {
        entry.count += 1;
      } else {
        map.set(key, { count: 1, previewUrl: f.imageUrl });
      }
    });
    return Array.from(map.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [biblioteca]);
  // Categoría que contiene la selección actual — feedback visual ahora
  // que el fondo elegido ya no tiene tile propio en el carrusel.
  const selectedCategory = React.useMemo(() => {
    if (!selection) return null;
    if (selection.mode === 'guardado') return MIS_FONDOS_CATEGORY;
    if (selection.mode === 'biblioteca') {
      const f = biblioteca.find((x) => x.id === selection.id);
      return f ? f.categoria || 'Otros' : null;
    }
    return null;
  }, [selection, biblioteca]);
  const categoryTile = (
    key: string,
    label: string,
    count: number,
    previewUrl: string,
  ) => {
    const isSel = selectedCategory === key;
    return (
      <button
        key={`cat-${key}`}
        type="button"
        onClick={() => onOpenCategory?.(key)}
        className={`${cardClass} rounded-2xl overflow-hidden relative bg-brand-black border-2 ${
          isSel ? 'border-brand-primary' : 'border-neutral-200'
        } transition-all active:scale-[0.97]`}
      >
        {previewUrl && (
          <CachedImg
            src={previewUrl}
            alt={label}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        )}
        {isSel && (
          <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
            <Check className="w-4 h-4" strokeWidth={3} />
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/65 via-black/15 to-transparent text-left">
          <p className="text-white text-[13px] font-semibold leading-tight drop-shadow truncate">
            {label}
          </p>
          <p className="text-white/75 text-[11px] drop-shadow">
            {count} {count === 1 ? 'fondo' : 'fondos'}
          </p>
        </div>
      </button>
    );
  };
  const categoryCarousel = (
    <HoverScrollCarousel
      className="flex gap-3 overflow-x-auto -mx-5 px-5 py-2 -my-2 scrollbar-hide"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
      arrowInset={4}
    >
      {[
        autoTile(cardClass, false),
        blancoTile(cardClass, false),
        categoryTile(
          MIS_FONDOS_CATEGORY,
          'Mis fondos',
          guardados.length,
          guardados[0]?.imageUrl || '',
        ),
        ...categorias.map((c) =>
          categoryTile(c.id, c.id, c.count, c.previewUrl),
        ),
        // Última card: cierra el carrusel con la salida a la grilla completa.
        ...(onVerMas
          ? [
              <AccionCard
                key="ver-mas"
                className={cardClass}
                sublabel={`${biblioteca.length + guardados.length} fondos`}
                onClick={onVerMas}
              />,
            ]
          : []),
      ]}
    </HoverScrollCarousel>
  );

  // Grid desktop del sheet — 2 columnas + card "Agregar fondo".
  const desktopGrid = (
    <div className="h-full overflow-y-auto -mx-1 px-1 py-1">
      <div className="grid grid-cols-2 gap-3">
        {(() => {
          const cards = renderCards('w-full aspect-[9/16]', true);
          return (
            <>
              {cards[0]}
              <AddOptionCard
                key="add-fondo"
                label="Agregar fondo"
                className="w-full aspect-[9/16]"
                options={addOptions}
                // Catálogo: la card va DERECHO a subir un fondo propio (un
                // popover de un solo ítem sería un click de más) y no puede
                // quedar "seleccionada" — lo que se sube se marca en su propia
                // card de fondo guardado.
                {...(soloAnadir
                  ? { onClick: onTapUpload }
                  : {
                      // Un fondo DESCRITO no genera card propia en el grid: sin
                      // esto la selección no queda marcada en ninguna parte.
                      selected: descripcionSelected,
                    })}
              />
              {cards.slice(1)}
            </>
          );
        })()}
      </div>
    </div>
  );

  // "Describir tu fondo" es la ÚNICA opción cuya selección no deja rastro en el
  // carrusel: un fondo descrito no genera card (los subidos sí, van a "Mis
  // fondos" y se marcan solos). Sin estado seleccionado acá, elegir un fondo
  // personalizado se veía igual que no haber elegido nada. Mismo tratamiento
  // que el resto: `border-2 border-brand-primary` + badge de check. El
  // `border-2` va SIEMPRE (neutral cuando no está elegido) para que marcarlo no
  // corra el layout un píxel.
  const ctaButtons = (
    <div className="flex flex-col gap-2.5">
      {/* En el catálogo no va el CTA de describir: ahí la segunda acción es
          subir un fondo propio (el botón de abajo). */}
      {!soloAnadir && (
      <button
        type="button"
        onClick={onTapDescribe}
        className={`w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border-2 text-brand-black/85 text-[14.5px] font-medium transition-colors ${
          descripcionSelected
            ? 'border-brand-primary'
            : 'border-neutral-200 hover:border-brand-primary/40'
        }`}
      >
        <span className="inline-flex items-center gap-2.5 min-w-0">
          <span className="w-7 h-7 shrink-0 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
            <TypeIcon className="w-3.5 h-3.5 text-brand-primary" />
          </span>
          Describir tu fondo
        </span>
        {descripcionSelected ? (
          <span className="w-6 h-6 shrink-0 rounded-full inline-flex items-center justify-center bg-brand-primary text-white shadow-md">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
        ) : (
          <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
        )}
      </button>
      )}
      <button
        type="button"
        onClick={onTapUpload}
        className="w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border-2 border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14.5px] font-medium transition-colors"
      >
        <span className="inline-flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
            <UserPlus className="w-3.5 h-3.5 text-brand-primary" />
          </span>
          Cargá tu propio fondo
        </span>
        <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
      </button>
    </div>
  );

  if (isSheet) {
    // Mobile: carrusel horizontal + CTAs abajo. Desktop: grid 2 columnas
    // con card "Agregar fondo" (popover describir / subir).
    return (
      <div className="flex flex-col h-full">
        <div className="md:hidden flex flex-col h-full">
          <div className="flex-1 flex items-center min-h-0">{carousel}</div>
          <div className="shrink-0 pt-4">{ctaButtons}</div>
        </div>
        <div className="hidden md:block h-full min-h-0">{desktopGrid}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {onOpenCategory ? categoryCarousel : carousel}
      {ajustesBlanco}
      {ctaButtons}
    </div>
  );
};

// ─── Fila de opciones tipo segmented control ──────────────────────────
// Chips en flujo (no anidados en otra card) a propósito: así no hay que resolver
// radios concéntricos y la fila respira igual que los CTAs de abajo.
//
// Sin modificadores de opacidad sobre colores de marca (`brand-primary/40`,
// `brand-black/85`): esos colores son CSS vars, así que Tailwind NO genera esas
// clases y quedan muertas (verificado contra dist/*.css). El estado seleccionado se
// resuelve con borde de marca + texto más oscuro; los grises salen de `neutral-*`.

const SegmentedRow: React.FC<{
  label: string;
  value: string;
  options: Array<{ id: string; label: string; icon?: React.ReactNode }>;
  onChange: (id: string) => void;
}> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <p className="text-[13px] font-semibold text-brand-black leading-tight">
      {label}
    </p>
    <div className="flex gap-2">
      {options.map((o) => {
        const selected = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(o.id)}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-4 rounded-lg border-2 bg-white text-[13.5px] transition-colors ${
              selected
                ? 'border-brand-primary text-brand-black font-semibold'
                : 'border-neutral-200 text-neutral-600 font-medium hover:border-neutral-300'
            }`}
          >
            {o.icon && (
              // `aria-hidden`: el icono es decorativo, el texto de al lado ya dice
              // qué es. El color lo hereda del botón, así que se oscurece solo
              // cuando la opción está elegida.
              <span aria-hidden className="shrink-0 inline-flex">
                {o.icon}
              </span>
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

/**
 * Ajustes del fondo blanco (tono + luz). Devuelve null si el fondo elegido no es
 * blanco, así que se puede montar sin condicionales del lado del consumidor.
 *
 * Vive acá y no dentro del picker porque tiene DOS puntos de montaje: el picker
 * inline (desktop) y la row plana de Fondo del panel mobile — donde el picker es un
 * sheet que se cierra al elegir, así que los controles tienen que quedar afuera.
 *
 * El tono NO es una instrucción para la IA: el backend mide el blanco de la imagen
 * generada y lo lleva a ese objetivo, así que el mismo preset da el MISMO tono en
 * toda la tienda (ADR-2026-08-14-fondo-blanco-consistente). La luz sí va al prompt.
 */
export const AjustesFondoBlanco: React.FC<{
  selection: FondoSelection | null;
  /** Recibe la selección COMPLETA ya mergeada, lista para setear. */
  onChange: (sel: FondoSelection) => void;
  className?: string;
}> = ({ selection, onChange, className }) => {
  if (!selection || selection.mode !== 'blanco') return null;
  const color: ColorFondoLiso = selection.colorLiso ?? 'blanco';
  const esBlanco = color === 'blanco';
  return (
    <div className={`flex flex-col gap-4 animate-mode-swap ${className ?? ''}`}>
      <ColorDelLiso
        value={color}
        onChange={(c) =>
          // El color se guarda SIEMPRE, también 'blanco': así volver al blanco desde
          // un color es un cambio explícito y no "borrar la propiedad".
          onChange({ ...selection, colorLiso: c })
        }
      />
      {/* Tono y luz existen sólo para el BLANCO: el tono es la normalización que hace
          el backend sobre el blanco generado y la luz es un bloque del prompt del modo
          'blanco'. Con beige/rosa/negro el fondo viaja como descripción, donde ninguno
          de los dos llega a ningún lado — mostrarlos sería ofrecer dos controles que
          no hacen nada. Ver ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo. */}
      {esBlanco && (
        <>
      <TemperaturaSlider
        value={selection.temperatura ?? TEMPERATURA_BLANCO_DEFAULT}
        onChange={(v) => onChange({ ...selection, temperatura: v })}
      />
      <SegmentedRow
        // "del estudio" y no "sobre el modelo": el mismo control viaja en el
        // catálogo, donde la tanda incluye packshots sin modelo.
        label="Luz del estudio"
        value={selection.iluminacion ?? ILUMINACION_BLANCO_DEFAULT}
        // Metáfora fotográfica de siempre: nublado = luz suave, sol directo = luz
        // dura. `strokeWidth` alto para que a 15px no se vean lavados.
        options={[
          {
            id: 'difusa',
            label: 'Difusa',
            icon: <CloudSun className="w-[15px] h-[15px]" strokeWidth={2.25} />,
          },
          {
            id: 'dura',
            label: 'Dura',
            icon: <Sun className="w-[15px] h-[15px]" strokeWidth={2.25} />,
          },
        ]}
        onChange={(v) =>
          onChange({ ...selection, iluminacion: v as IluminacionBlanco })
        }
      />
        </>
      )}
    </div>
  );
};

/**
 * Los cuatro colores del fondo liso, como swatches.
 *
 * Swatches y no un `SegmentedRow` de texto: lo que se elige es un color, y el color se
 * reconoce viéndolo. El negro lleva el tilde en blanco y los claros en negro, para que
 * se vea sobre cualquiera de los dos.
 */
const ColorDelLiso: React.FC<{
  value: ColorFondoLiso;
  onChange: (c: ColorFondoLiso) => void;
}> = ({ value, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <p className="text-[13px] font-semibold text-brand-black leading-tight">
      Color del fondo
    </p>
    <div className="flex items-center gap-2 flex-wrap">
      {COLORES_FONDO_LISO.map((c) => {
        const elegido = c.id === value;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(c.id)}
            aria-pressed={elegido}
            title={c.label}
            className={`relative w-9 h-9 rounded-full border transition-all active:scale-95 ${
              elegido
                ? 'border-brand-primary ring-2 ring-[rgb(var(--brand-primary-rgb)_/_0.3)]'
                : 'border-neutral-300 hover:border-brand-primary'
            }`}
            style={{ backgroundColor: c.hex }}
          >
            {elegido && (
              <Check
                className={`absolute inset-0 m-auto w-4 h-4 ${
                  c.id === 'negro' ? 'text-white' : 'text-brand-black/70'
                }`}
                strokeWidth={3}
              />
            )}
            <span className="sr-only">{c.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

// ─── Slider de temperatura ────────────────────────────────────────────
// Tres paradas, no continuo: el backend normaliza a uno de tres RGB objetivo, así
// que un valor intermedio no existiría. El gesto lo maneja un `input[type=range]`
// nativo transparente encima del riel dibujado (mismo patrón que UltraTierSlider en
// pricing/PlanCard): se arrastra, se mueve con las flechas y anuncia su valor sin
// reimplementar nada a mano.
//
// El riel lleva el degradé frío→neutro→cálido. Los tonos están EXAGERADOS respecto
// de los objetivos reales del backend (OBJETIVO_BLANCO en
// atlas-backend/src/lib/fondoBlanco.ts): esos son 246,250,255 y 255,250,242, o sea
// menos de 10 puntos de diferencia sobre blanco — pintados a escala real el riel se
// veía blanco liso y el control no comunicaba nada. Acá el degradé indica la
// DIRECCIÓN del ajuste, no su magnitud; el resultado real es mucho más sutil.

const TEMPERATURA_OPCIONES: Array<{ id: TemperaturaBlanco; label: string }> = [
  { id: 'frio', label: 'Frío' },
  { id: 'neutro', label: 'Neutro' },
  { id: 'calido', label: 'Cálido' },
];

const TemperaturaSlider: React.FC<{
  value: TemperaturaBlanco;
  onChange: (v: TemperaturaBlanco) => void;
}> = ({ value, onChange }) => {
  const max = TEMPERATURA_OPCIONES.length - 1;
  const index = Math.max(
    0,
    TEMPERATURA_OPCIONES.findIndex((o) => o.id === value),
  );
  const pct = (index / max) * 100;
  const THUMB = 22;

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[13px] font-semibold text-brand-black leading-tight">
        Tono del blanco
      </p>

      <div className="relative h-7 mt-1">
        {/* Riel con el degradé de los tonos reales */}
        <div
          className="absolute inset-x-0 top-1/2 h-[8px] -translate-y-1/2 rounded-full border border-neutral-200"
          style={{
            background:
              'linear-gradient(90deg, rgb(150,196,255) 0%, rgb(255,255,255) 50%, rgb(255,201,125) 100%)',
          }}
        />
        {/* Paradas: la elegida se pinta con el color de marca */}
        {/* `px-[8px]` = mitad de la manija (11) menos mitad del punto (3): así los
            puntos quedan exactamente bajo el centro de la manija en cada parada. */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-[8px]">
          {TEMPERATURA_OPCIONES.map((o, i) => (
            <span
              key={o.id}
              className={`w-[6px] h-[6px] rounded-full transition-colors ${
                i === index ? 'bg-brand-primary' : 'bg-neutral-300'
              }`}
            />
          ))}
        </div>
        {/* Manija */}
        <div
          className="pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full bg-white border-2 border-brand-primary shadow-md transition-[left] duration-200 ease-out"
          style={{
            width: THUMB,
            height: THUMB,
            left: `calc(${pct}% - ${(pct / 100) * THUMB}px)`,
          }}
        />
        <input
          type="range"
          min={0}
          max={max}
          step={1}
          value={index}
          onChange={(e) =>
            onChange(TEMPERATURA_OPCIONES[Number(e.target.value)]!.id)
          }
          aria-label="Tono del blanco"
          aria-valuetext={TEMPERATURA_OPCIONES[index]!.label}
          className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
          // Sin esto, arrastrar el slider en mobile paneaba el carrusel de fondos
          // que lo contiene en vez de mover el tono (mismo caso que el slider de
          // planes dentro del carrusel de pricing).
          style={{ touchAction: 'none' }}
        />
      </div>

      {/* Etiquetas alineadas con las paradas */}
      <div className="flex justify-between">
        {TEMPERATURA_OPCIONES.map((o, i) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={`text-[12px] transition-colors ${
              i === index
                ? 'text-brand-black font-semibold'
                : 'text-neutral-500 font-medium hover:text-neutral-700'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Describe view ────────────────────────────────────────────────────

// ─── Chat message row (mobile only) ──────────────────────────────────

const ChatMessageRow: React.FC<{
  msg: DescribeChatMessage;
  onUse: (prompt: string) => void;
}> = ({ msg, onUse }) => {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end animate-mode-swap">
        <div className="max-w-[80%] bg-[#F2F2F7] rounded-[18px] rounded-tr-[6px] px-4 py-2.5 text-[14.5px] text-brand-black/90 leading-snug whitespace-pre-wrap">
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2.5 animate-mode-swap">
      {msg.text && (
        <p className="text-[14.5px] text-brand-black/85 leading-relaxed whitespace-pre-wrap">
          {msg.text}
        </p>
      )}
      {msg.suggestions?.map((s, i) => (
        <div
          key={i}
          className="rounded-2xl border border-neutral-200 bg-white p-3.5 flex flex-col gap-2.5"
        >
          {s.titulo && (
            <p className="text-[13px] font-semibold text-brand-black/85 leading-tight">
              {s.titulo}
            </p>
          )}
          <p className="text-[14px] text-brand-black/80 leading-relaxed whitespace-pre-wrap">
            {s.prompt}
          </p>
          <button
            type="button"
            onClick={() => onUse(s.prompt)}
            className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-semibold shadow-sm active:scale-[0.97] transition-transform"
          >
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
            Utilizar
          </button>
        </div>
      ))}
    </div>
  );
};

export interface DescribeViewProps {
  initial?: string;
  onBack: () => void;
  onConfirm: (text: string) => void;
  /** Si está provisto, el componente reporta cada cambio al padre. El
   *  padre puede usarlo para habilitar/disparar un botón "Usar" en el
   *  header (mobile sheet) en vez del CTA inline. */
  onTextChange?: (text: string) => void;
  /** Nombres de los productos seleccionados — el chat AI los usa como
   *  contexto en el system prompt. */
  selectedProductNames?: string[];
  /** Rubro de la empresa — también va al system prompt. */
  rubro?: string | null;
  /** Estado del chat mode (mobile only). Lifteado al sheet para que
   *  el header pueda ocultar el botón "Usar" mientras está chateando. */
  chatMode?: boolean;
  onChatModeChange?: (b: boolean) => void;
}

/**
 * Se exporta porque el panel lateral de Modelo/Catálogo (`FondoGrilla`) abre este MISMO
 * formulario en una vista propia: describir el fondo se hace en un lugar solo, con su
 * chat, y una segunda implementación inline daba otra experiencia según por dónde entrás
 * (ADR-2026-09-02-describir-fondo-vuelve-al-chat).
 */
export const DescribeView: React.FC<DescribeViewProps> = ({
  initial,
  onBack,
  onConfirm,
  onTextChange,
  selectedProductNames = [],
  rubro = null,
  chatMode = false,
  onChatModeChange,
}) => {
  const [text, setText] = useState<string>(initial || '');
  const submit = () => onConfirm(text.trim());
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mobileTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  // Auto-grow del textarea: arranca con altura para 3 filas y crece
  // hasta 15 antes de scrollear. Calculamos el cap leyendo line-height
  // y padding del propio elemento (robusto a cambios de tipografía).
  const adjustTextareaHeight = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    const styles = window.getComputedStyle(el);
    const lineHeight =
      parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.4;
    const padY =
      (parseFloat(styles.paddingTop) || 0) +
      (parseFloat(styles.paddingBottom) || 0);
    const borderY =
      (parseFloat(styles.borderTopWidth) || 0) +
      (parseFloat(styles.borderBottomWidth) || 0);
    const maxHeight = lineHeight * 15 + padY + borderY;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };
  const handleChange = (v: string) => {
    setText(v);
    onTextChange?.(v);
  };
  // Re-ajustar el alto cuando cambia el texto. Aplica a ambos textareas
  // (solo uno está visible por viewport, pero ambos viven en el DOM).
  useEffect(() => {
    adjustTextareaHeight(mobileTextareaRef.current);
    adjustTextareaHeight(textareaRef.current);
  }, [text]);
  // Inicial: ajustamos en mount y cada vez que salimos de chatMode (el
  // textarea reaparece y puede haber cambiado de tamaño contenedor).
  useEffect(() => {
    const t = setTimeout(() => {
      adjustTextareaHeight(mobileTextareaRef.current);
      adjustTextareaHeight(textareaRef.current);
    }, 0);
    return () => clearTimeout(t);
  }, [chatMode]);
  // Auto-focus al entrar — el user llega a esta view explícitamente
  // para escribir. Ambos textareas (mobile + desktop) están en el DOM;
  // elegimos el visible vía matchMedia. Pequeño delay para no pelear
  // con la animación de entrada del sheet.
  useEffect(() => {
    if (chatMode) return; // en chatMode el textarea de arriba ya no está visible
    const t = setTimeout(() => {
      const isDesktop =
        typeof window !== 'undefined' &&
        window.matchMedia('(min-width: 768px)').matches;
      const el = isDesktop ? textareaRef.current : mobileTextareaRef.current;
      el?.focus();
    }, 80);
    return () => clearTimeout(t);
  }, [chatMode]);

  // ─── Chat (mobile only) ──────────────────────────────────────────────
  const chat = useDescribeChat({
    productNames: selectedProductNames,
    rubro,
  });
  const [chatInput, setChatInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, [chat.messages, chat.streaming]);

  const startChat = async () => {
    if (chat.streaming) return;
    const hasText = text.trim().length > 0;
    const initialPrompt = hasText
      ? `Ayudame a mejorar esta idea de fondo: "${text.trim()}"`
      : 'Sugerime ideas de fondos.';
    onChatModeChange?.(true);
    await chat.start(initialPrompt);
  };

  const handleSendFollowUp = async () => {
    const msg = chatInput.trim();
    if (!msg) return;
    setChatInput('');
    await chat.sendFollowUp(msg);
  };

  const aiButtonDisabled = chat.streaming;
  const aiButtonLabel = text.trim()
    ? 'Ayudame a mejorar mi idea'
    : 'Sugerime ideas';

  return (
    <>
      {/* ── Mobile: textarea inicial + botón AI. Al tocar el botón,
          entra en chatMode: messages flex-1 arriba, input al bottom. ── */}
      <div className="md:hidden h-full flex flex-col">
        {/* Vista inicial — colapsa cuando entra chatMode */}
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            chatMode ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
          }`}
        >
          <div className="min-h-0">
            {/* El botón vive DENTRO del cuadro de texto (esquina inferior
                derecha): el `pb-11` del textarea le reserva el lugar para que
                el texto no le pase por abajo. */}
            <div className="relative">
              <textarea
                ref={mobileTextareaRef}
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                placeholder="Ej: estudio minimalista con luz natural lateral, fondo gris suave…"
                rows={3}
                className="w-full resize-none rounded-2xl border border-neutral-200 bg-white px-4 pt-3.5 pb-11 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
              />
              {/* Este historial (tipo 'escena') lo escribe el BACKEND cuando la
                  generación manda `background_mode: 'descripcion'`, así que acá
                  sólo se lee — no hace falta registrar nada al confirmar. */}
              <PromptRecientesButton
                tipo="escena"
                onPick={handleChange}
                titulo="Fondos que describiste antes"
                className="absolute bottom-2.5 right-2.5"
              />
            </div>
            <button
              type="button"
              onClick={startChat}
              disabled={aiButtonDisabled}
              className="relative mt-3 w-full overflow-hidden rounded-2xl py-3 px-4 text-[14.5px] font-semibold text-white shadow-sm bg-white border border-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            >
              {/* Blurs subtítulos — anclados al bottom, asomando hacia
                  abajo. Misma idea que el AutoPlaceholder de las cards
                  miniatura Modelo/Pose: opacidades bajas para que el
                  fondo del botón siga siendo blanco. */}
              <span className="pointer-events-none absolute inset-0">
                <span
                  className="absolute rounded-full blur-2xl animate-drift-1"
                  style={{
                    width: '50%',
                    height: '180%',
                    backgroundColor: 'var(--brand-primary)',
                    left: '-5%',
                    top: '-40%',
                    opacity: 0.9,
                  }}
                />
                <span
                  className="absolute rounded-full blur-2xl animate-drift-2"
                  style={{
                    width: '45%',
                    height: '170%',
                    backgroundColor: '#06B6D4',
                    right: '0%',
                    top: '-50%',
                    opacity: 0.8,
                  }}
                />
                <span
                  className="absolute rounded-full blur-2xl animate-drift-3"
                  style={{
                    width: '55%',
                    height: '180%',
                    backgroundColor: '#EC4899',
                    left: '30%',
                    top: '-40%',
                    opacity: 0.85,
                  }}
                />
              </span>
              <span className="relative z-10 inline-flex items-center justify-center gap-2 drop-shadow">
                <Sparkles className="w-4 h-4 text-white" />
                {aiButtonLabel}
              </span>
            </button>
          </div>
        </div>
        {/* Chat — aparece cuando chatMode es true. Crece de 0 a 1fr con
            transición sobre grid-template-rows para que se sienta como el
            input bajando. */}
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            chatMode ? 'grid-rows-[1fr] flex-1' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto -mx-5 px-5">
              <div className="flex flex-col gap-3 py-1">
                {chat.messages.map((m, i) => {
                  const isLast = i === chat.messages.length - 1;
                  const showDots =
                    chat.streaming &&
                    isLast &&
                    m.role === 'assistant' &&
                    !m.text &&
                    !m.suggestions;
                  return (
                    <React.Fragment key={i}>
                      {showDots ? (
                        <div className="flex items-center gap-1.5 py-1">
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '0ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '150ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '300ms' }}
                          />
                        </div>
                      ) : (
                        <ChatMessageRow
                          msg={m}
                          onUse={(prompt) => onConfirm(prompt)}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>
        </div>
        {/* Input al bottom — visible solo en chatMode. */}
        {chatMode && (
          <div className="pt-3 shrink-0">
            <div className="bg-white rounded-2xl shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04] focus-within:ring-brand-primary/25 transition-[box-shadow,--tw-ring-color] duration-200">
              <textarea
                ref={chatInputRef}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendFollowUp();
                  }
                }}
                placeholder="Afiná tu idea con Atlas…"
                rows={1}
                disabled={chat.streaming}
                className="w-full resize-none bg-transparent outline-none px-4 pt-4 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 leading-snug"
              />
              <div className="flex items-center justify-end gap-2 px-3 pb-2.5 pt-2">
                <button
                  type="button"
                  onClick={handleSendFollowUp}
                  disabled={chatInput.trim().length === 0 || chat.streaming}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                    chatInput.trim()
                      ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                      : 'bg-neutral-100 text-brand-black/70'
                  }`}
                  aria-label="Enviar"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Desktop: mismo flujo que mobile (textarea + botón AI →
          chatMode con messages y chat input). Cancelar/Aceptar footer
          solo se muestra fuera de chatMode. ── */}
      <div className="hidden md:flex md:flex-col md:min-h-full md:h-full">
        {/* Vista inicial — colapsa con grid-template-rows transición. */}
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            chatMode ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
          }`}
        >
          <div className="min-h-0">
            {/* Acá el "cuadro" es la card blanca, no el textarea: el botón va
                adentro de la card, alineado a la derecha. */}
            <div className="bg-white border border-neutral-200 rounded-lg p-4 flex flex-col">
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                placeholder="Describí la escena que querés para tus imágenes"
                rows={5}
                className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent"
              />
              <PromptRecientesButton
                tipo="escena"
                onPick={handleChange}
                titulo="Fondos que describiste antes"
                className="self-end -mr-1 -mb-1"
              />
            </div>
            <button
              type="button"
              onClick={startChat}
              disabled={aiButtonDisabled}
              className="relative mt-3 w-full overflow-hidden rounded-2xl py-3 px-4 text-[14.5px] font-semibold text-white shadow-sm bg-white border border-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            >
              <span className="pointer-events-none absolute inset-0">
                <span
                  className="absolute rounded-full blur-2xl animate-drift-1"
                  style={{
                    width: '50%',
                    height: '180%',
                    backgroundColor: 'var(--brand-primary)',
                    left: '-5%',
                    top: '-40%',
                    opacity: 0.9,
                  }}
                />
                <span
                  className="absolute rounded-full blur-2xl animate-drift-2"
                  style={{
                    width: '45%',
                    height: '170%',
                    backgroundColor: '#06B6D4',
                    right: '0%',
                    top: '-50%',
                    opacity: 0.8,
                  }}
                />
                <span
                  className="absolute rounded-full blur-2xl animate-drift-3"
                  style={{
                    width: '55%',
                    height: '180%',
                    backgroundColor: '#EC4899',
                    left: '30%',
                    top: '-40%',
                    opacity: 0.85,
                  }}
                />
              </span>
              <span className="relative z-10 inline-flex items-center justify-center gap-2 drop-shadow">
                <Sparkles className="w-4 h-4 text-white" />
                {aiButtonLabel}
              </span>
            </button>
          </div>
        </div>
        {/* Chat — aparece cuando chatMode es true. */}
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            chatMode ? 'grid-rows-[1fr] flex-1' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto -mx-5 px-5">
              <div className="flex flex-col gap-3 py-1">
                {chat.messages.map((m, i) => {
                  const isLast = i === chat.messages.length - 1;
                  const showDots =
                    chat.streaming &&
                    isLast &&
                    m.role === 'assistant' &&
                    !m.text &&
                    !m.suggestions;
                  return (
                    <React.Fragment key={i}>
                      {showDots ? (
                        <div className="flex items-center gap-1.5 py-1">
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '0ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '150ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '300ms' }}
                          />
                        </div>
                      ) : (
                        <ChatMessageRow
                          msg={m}
                          onUse={(prompt) => onConfirm(prompt)}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {/* Input chat al bottom — visible solo en chatMode. `pb-4` da
            aire abajo en desktop: el scroll wrapper del sheet usa
            md:pb-0 y el shadow del input se cortaba contra el borde. */}
        {chatMode && (
          <div className="pt-3 pb-4 shrink-0">
            <div className="bg-white rounded-2xl shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04] focus-within:ring-brand-primary/25 transition-[box-shadow,--tw-ring-color] duration-200">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendFollowUp();
                  }
                }}
                placeholder="Afiná tu idea con Atlas…"
                rows={1}
                disabled={chat.streaming}
                className="w-full resize-none bg-transparent outline-none px-4 pt-4 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 leading-snug"
              />
              <div className="flex items-center justify-end gap-2 px-3 pb-2.5 pt-2">
                <button
                  type="button"
                  onClick={handleSendFollowUp}
                  disabled={chatInput.trim().length === 0 || chat.streaming}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                    chatInput.trim()
                      ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                      : 'bg-neutral-100 text-brand-black/70'
                  }`}
                  aria-label="Enviar"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Footer Cancelar/Aceptar — solo fuera de chatMode. */}
        {!chatMode && (
          <div className="sticky bottom-0 mt-auto -mx-5 px-5 pt-3 pb-3 bg-white flex justify-end gap-3 border-t border-black/[0.06]">
            <button
              type="button"
              onClick={onBack}
              className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={text.trim().length === 0}
              className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
                text.trim()
                  ? 'text-white bg-brand-primary hover:bg-brand-primary-hover'
                  : 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
              }`}
            >
              Utilizar
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Upload view ──────────────────────────────────────────────────────

interface UploadViewProps {
  companyId: string | null;
  onConfirm: (saved: FondoGuardado) => void;
  onFondoSaved: () => void;
  /**
   * La pantalla ocupa una ventana ANCHA (el selector de fuentes de la caja del Agente,
   * 980): la imagen y el nombre a la izquierda, los tips a la derecha, y el CTA en una
   * barra de borde a borde — ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-
   * columnas. El título lo pone el host.
   */
  ancho?: boolean;
}

/**
 * Subir un fondo propio: file picker + preview + nombre + Confirmar. Además del sheet y
 * el inline de acá, la usa la biblioteca de adjuntos del Agente
 * (ADR-2026-09-12-la-biblioteca-agrega-en-todas-las-pestanas): una sola versión.
 */
export const FondoUploadView: React.FC<UploadViewProps> = ({
  companyId,
  onConfirm,
  onFondoSaved,
  ancho = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Aceptar la imagen elegida, venga del input o de la zona de subida del modo ancho.
  // Convertir HEIC/HEIF/AVIF→JPEG acá y no sólo en `uploadFondo`: el preview es un
  // <img> del archivo crudo y el navegador no renderiza HEIC, así que sin esto el
  // preview quedaba roto.
  const aceptarArchivo = async (f: File) => {
    setError(null);
    try {
      setConverting(true);
      setFile(await normalizeImageForUpload(f));
    } catch (convErr) {
      console.error('[FondoPicker] conversión de formato falló', convErr);
      setError(
        convErr instanceof UnsupportedImageFormatError
          ? convErr.message
          : 'No pudimos procesar esa imagen. Probá con otra (JPG o PNG).',
      );
    } finally {
      setConverting(false);
    }
  };

  const handleConfirm = async () => {
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar la empresa.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const saved = await uploadFondo({ companyId, file, name });
      onFondoSaved();
      onConfirm(saved);
    } catch (err) {
      console.error('[FondoPicker] uploadFondo error', err);
      setError(
        err instanceof Error
          ? err.message
          : 'No pudimos subir el fondo. Probá de nuevo.',
      );
    } finally {
      setUploading(false);
    }
  };

  // ── Ancho: dos columnas + barra de CTA (selector de fuentes, 980) ────
  // ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas. El fondo es una
  // imagen apaisada: la columna de trabajo se la queda entera y el preview crece con
  // ella, en vez del recuadro de media ventana que había antes.
  if (ancho) {
    return (
      <>
        <ColumnasDeAlta>
          <ColumnaDeTrabajo>
            {!file ? (
              <ZonaDeSubida
                alto="alta"
                titulo={converting ? 'Convirtiendo…' : 'Subí la imagen del fondo'}
                ayuda="Arrastrala acá o hacé click para elegirla."
                deshabilitada={converting}
                onArchivos={(archivos) => {
                  const elegida = archivos[0];
                  if (elegida) void aceptarArchivo(elegida);
                }}
              />
            ) : (
              <div className="relative flex min-h-[220px] flex-1 items-center justify-center overflow-hidden rounded-2xl bg-neutral-100 ring-1 ring-black/[0.05]">
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Fondo"
                    /* Llena la caja que le deja la columna: el fondo es lo que se está
                       eligiendo y verlo chico no ayuda a decidir. */
                    className="block h-full max-h-full w-full object-contain"
                  />
                )}
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  aria-label="Quitar"
                  className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/70"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {file && (
              <div>
                <label className="mb-1.5 block text-[12.5px] font-medium uppercase tracking-wide text-[#3C3C43]/55">
                  Nombre del fondo (opcional)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Terraza al atardecer"
                  disabled={uploading}
                  className="h-11 w-full rounded-xl border border-neutral-200 bg-white px-4 text-[14.5px] text-brand-black/90 transition-colors placeholder:text-[#3C3C43]/40 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:opacity-50"
                />
              </div>
            )}
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700">
                {error}
              </div>
            )}
          </ColumnaDeTrabajo>
          <AsideDeAlta titulo="Qué funciona como fondo">
            <TipsDeAlta
              items={[
                'El lugar o escenario donde querés que aparezca tu producto, sin el producto adentro.',
                'Apaisada y con espacio libre en el medio: ahí es donde entra lo que generes.',
                'Una foto nítida y bien iluminada se recrea mejor que una oscura o movida.',
              ]}
            />
          </AsideDeAlta>
        </ColumnasDeAlta>
        <PieDeAlta>
          <BotonPrincipalDeAlta onClick={handleConfirm} disabled={!file || uploading}>
            {uploading ? 'Subiendo…' : 'Utilizar este fondo'}
          </BotonPrincipalDeAlta>
        </PieDeAlta>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:min-h-full">
      {/* Intro — solo mobile (en desktop el header del sheet ya tiene el
          título y el padding interno alcanza). */}
      <div className="md:hidden">
        <p className="text-[15.5px] font-semibold text-brand-black/90">
          Subí tu fondo
        </p>
        <p className="text-[13px] text-[#3C3C43]/60 mt-0.5 leading-relaxed">
          Una imagen del lugar / escenario que querés usar. Queda guardada
          en tu biblioteca para reusarla después.
        </p>
      </div>
      <p className="hidden md:block text-[15px] text-neutral-500">
        Una imagen del lugar / escenario que querés usar. Queda guardada en
        tu biblioteca para reusarla después.
      </p>
      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={converting}
          className="w-full inline-flex flex-col items-center justify-center gap-2 py-10 rounded-2xl border-2 border-dashed border-neutral-300 hover:border-brand-primary/40 bg-white text-[#3C3C43]/55 hover:text-brand-primary transition-colors disabled:opacity-60 disabled:cursor-wait"
        >
          <Upload className="w-6 h-6" />
          <span className="text-[14px] font-medium">
            {converting ? 'Convirtiendo…' : 'Subir imagen'}
          </span>
        </button>
      ) : (
        <div className="relative rounded-2xl overflow-hidden bg-neutral-100 flex items-center justify-center max-h-[42dvh]">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Fondo"
              className="block max-w-full max-h-[42dvh] w-auto h-auto object-contain"
            />
          )}
          <button
            type="button"
            onClick={() => setFile(null)}
            aria-label="Quitar"
            className="absolute top-2 right-2 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) await aceptarArchivo(f);
        }}
      />
      {file && (
        <div>
          <label className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
            Nombre del fondo
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Opcional"
            disabled={uploading}
            className="w-full h-11 px-4 rounded-xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50 transition-colors"
          />
        </div>
      )}
      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
          {error}
        </div>
      )}
      {file && (
        <>
          {/* Mobile: botón pill full-width "Utilizar". */}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={uploading}
            className="md:hidden w-full inline-flex items-center justify-center py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
          >
            {uploading ? 'Subiendo…' : 'Utilizar'}
          </button>
          {/* Desktop: footer al bottom (mt-auto + sticky). */}
          <div className="hidden md:flex sticky bottom-0 mt-auto -mx-5 px-5 pt-3 pb-3 bg-white justify-end gap-3 border-t border-black/[0.06]">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={uploading}
              className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
                uploading
                  ? 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
                  : 'text-white bg-brand-primary hover:bg-brand-primary-hover'
              }`}
            >
              {uploading ? 'Subiendo…' : 'Aceptar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Inline (desktop) ─────────────────────────────────────────────────

interface FondoPickerInlineProps {
  selection: FondoSelection | null;
  onChange: (sel: FondoSelection) => void;
  view: FondoPickerView;
  onViewChange: (v: FondoPickerView) => void;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  companyId: string | null;
  onFondoSaved: () => void;
  /** Si true, oculta el label "Fondo" interno (lo provee el caller). */
  hideLabel?: boolean;
  /** Opcional: si está provisto, los botones "Describí el fondo" / "Cargá
   *  tu fondo" llaman a este callback en vez de cambiar el view inline.
   *  Lo usa design-modelo en desktop para abrir el sheet como side panel.
   *  `initialCategory` abre la vista 'all' directo en esa categoría
   *  (cards de categorías del carrusel inline). */
  onOpenSheet?: (
    initialView: FondoPickerView,
    initialCategory?: string,
  ) => void;
  /** Nombres de productos seleccionados — contexto para el chat AI. */
  selectedProductNames?: string[];
  /** Rubro de la empresa — contexto para el chat AI. */
  rubro?: string | null;
  /**
   * Esconde las DOS salidas a "Ver más" (el link del encabezado y la card al final del
   * carrusel) sin apagar `onOpenSheet`, que es lo que además dibuja las CATEGORÍAS.
   *
   * Lo usa el panel lateral del modo Modelo: ahí las categorías YA son la vista
   * principal y cada una abre el sheet filtrada, así que dos atajos más a "todos los
   * fondos" repetían el mismo destino tres veces en la misma caja.
   */
  sinVerMas?: boolean;
}

export const FondoPickerInline: React.FC<FondoPickerInlineProps> = ({
  selection,
  onChange,
  view,
  onViewChange,
  biblioteca,
  guardados,
  companyId,
  onFondoSaved,
  hideLabel = false,
  onOpenSheet,
  sinVerMas = false,
  selectedProductNames = [],
  rubro = null,
}) => {
  // Si view !== 'list' siempre mostramos "Volver" — sin label aunque
  // hideLabel sea true, ese botón es de navegación dentro del picker.
  // En 'list' + hideLabel + onOpenSheet provisto, mostramos "Ver más" a
  // la derecha (mismo patrón que PosePickerInline) que abre el sheet en
  // vista 'all' (grid completa de todos los fondos).
  const showVerMas =
    !sinVerMas && hideLabel && view === 'list' && !!onOpenSheet;
  const showHeader = !hideLabel || view !== 'list' || showVerMas;
  return (
    <div className="flex flex-col gap-3">
      {showHeader && (
        <div
          // Sin label, lo único que queda en la fila es el "Volver" —el "Ver más" puede
          // estar apagado con `sinVerMas`—, y ése es navegación: va a la IZQUIERDA. A la
          // derecha quedaba debajo de la X del panel lateral, y los dos juntos se leían
          // como el mismo control repetido (ADR-2026-08-24-el-volver-va-a-la-izquierda).
          className={`flex items-center ${
            !hideLabel
              ? 'justify-between'
              : showVerMas
                ? 'justify-between'
                : 'justify-start'
          }`}
        >
          {!hideLabel && (
            <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
              Fondo
            </p>
          )}
          {view !== 'list' && (
            <button
              type="button"
              onClick={() => onViewChange('list')}
              className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Volver
            </button>
          )}
          {showVerMas && (
            <button
              type="button"
              onClick={() => onOpenSheet && onOpenSheet('all')}
              className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
            >
              Ver más
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {view === 'list' && (
        <ListView
          selection={selection}
          biblioteca={biblioteca}
          guardados={guardados}
          variant="inline"
          onSelectAuto={() => onChange({ mode: 'auto' })}
          // Re-tocar el tile no debe perder el tono/luz ya elegidos.
          onSelectBlanco={() =>
            onChange(
              selection?.mode === 'blanco' ? selection : { mode: 'blanco' },
            )
          }
          ajustesBlanco={
            <AjustesFondoBlanco selection={selection} onChange={onChange} />
          }
          onSelectBiblioteca={(f) =>
            onChange({
              mode: 'biblioteca',
              id: f.id,
              name: f.nombre,
              imageUrl: f.imageUrl,
            })
          }
          onSelectGuardado={(f) =>
            onChange({
              mode: 'guardado',
              fileName: f.fileName,
              name: f.nombre,
              imageUrl: f.imageUrl,
            })
          }
          onTapDescribe={() =>
            onOpenSheet ? onOpenSheet('describe') : onViewChange('describe')
          }
          onTapUpload={() =>
            onOpenSheet ? onOpenSheet('upload') : onViewChange('upload')
          }
          onOpenCategory={
            onOpenSheet ? (c) => onOpenSheet('all', c) : undefined
          }
          onVerMas={
            onOpenSheet && !sinVerMas ? () => onOpenSheet('all') : undefined
          }
        />
      )}
      {view === 'describe' && (
        <DescribeView
          initial={selection?.mode === 'descripcion' ? selection.text : ''}
          onBack={() => onViewChange('list')}
          onConfirm={(text) => {
            onChange({ mode: 'descripcion', text });
            onViewChange('list');
          }}
          selectedProductNames={selectedProductNames}
          rubro={rubro}
        />
      )}
      {view === 'upload' && (
        <FondoUploadView
          companyId={companyId}
          onFondoSaved={onFondoSaved}
          onConfirm={(saved) => {
            onChange({
              mode: 'guardado',
              fileName: saved.fileName,
              name: saved.nombre,
              imageUrl: saved.imageUrl,
            });
            onViewChange('list');
          }}
        />
      )}
    </div>
  );
};

// ─── AllFondosView ────────────────────────────────────────────────────
// Menú raíz de fondos del sheet (mobile) y de la vista "Ver más"
// (desktop). Dos niveles:
//   1. Lista vertical de cards, una debajo de la otra: "Fondo
//      automático" arriba de todo, "Fondo blanco", "Mis fondos"
//      (guardados + card añadir) y una card por categoría de
//      biblioteca (preview + cantidad).
//   2. Tocar una categoría navega al detalle: grilla de fondos estilo
//      PruebaNuevaInterfaz (2 col mobile / 3 desktop).
// El nivel activo (`category`) y el draft de selección viven en el
// sheet: el header iOS mobile muestra Volver / título / Aceptar según
// el nivel; en desktop el footer (hidden md:flex) hace Cancelar/Aceptar.

/** Id de la pseudo-categoría con los fondos guardados del user. */
const MIS_FONDOS_CATEGORY = 'mis-fondos';

/** Título visible de una categoría del menú de fondos. */
const fondoCategoryLabel = (c: string): string =>
  c === MIS_FONDOS_CATEGORY ? 'Mis fondos' : c;

interface AllFondosViewProps {
  draft: FondoSelection | null;
  onDraftChange: (sel: FondoSelection) => void;
  /** Categoría activa (null = lista de categorías). Lifteada al sheet
   *  para que el header mobile pueda mostrar Volver + título. */
  category: string | null;
  onCategoryChange: (c: string | null) => void;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  onConfirm: (sel: FondoSelection) => void;
  onCancel: () => void;
  onTapDescribe: () => void;
  onTapUpload: () => void;
  /**
   * Qué va en la card de arriba a la derecha, al lado de "Fondo automático".
   *
   * 'describir' (default) — el flujo de siempre: escribir la escena en palabras.
   * 'anadir' — subir un fondo propio. Es lo que usa el CATÁLOGO: ahí el fondo se
   * elige una vez para toda la tanda, y el pedido fue que esa segunda card sea la
   * de sumar un fondo propio en vez de la de describir uno.
   */
  accionSecundaria?: 'describir' | 'anadir';
  /** Ajustes del fondo blanco (`AjustesFondoBlanco`), pegados a la opción "Fondo
   *  blanco" de la que dependen. Slot y no render propio para que el sheet los
   *  ate a SU draft: acá la selección se confirma con "Aceptar". */
  ajustesBlanco?: React.ReactNode;
}

const AllFondosView: React.FC<AllFondosViewProps> = ({
  draft,
  onDraftChange,
  category,
  onCategoryChange,
  biblioteca,
  guardados,
  onConfirm,
  onCancel,
  onTapDescribe,
  onTapUpload,
  accionSecundaria = 'describir',
  ajustesBlanco,
}) => {
  // Categorías de biblioteca (sin categoría → 'Otros') con preview
  // (primer fondo) y cantidad, para las cards del nivel 1.
  const categorias = React.useMemo(() => {
    const map = new Map<string, { count: number; previewUrl: string }>();
    biblioteca.forEach((f) => {
      const key = f.categoria || 'Otros';
      const entry = map.get(key);
      if (entry) {
        entry.count += 1;
      } else {
        map.set(key, { count: 1, previewUrl: f.imageUrl });
      }
    });
    return Array.from(map.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [biblioteca]);

  // Items del detalle según la categoría activa.
  type Item =
    | { kind: 'biblioteca'; f: FondoBiblioteca }
    | { kind: 'guardado'; f: FondoGuardado };
  const items: Item[] = React.useMemo(() => {
    if (category === MIS_FONDOS_CATEGORY) {
      return guardados.map((f): Item => ({ kind: 'guardado', f }));
    }
    return biblioteca
      .filter((f) => (f.categoria || 'Otros') === category)
      .map((f): Item => ({ kind: 'biblioteca', f }));
  }, [category, biblioteca, guardados]);

  const isItemSelected = (it: Item): boolean => {
    if (!draft) return false;
    if (it.kind === 'biblioteca')
      return draft.mode === 'biblioteca' && draft.id === it.f.id;
    return draft.mode === 'guardado' && draft.fileName === it.f.fileName;
  };
  const selectItem = (it: Item) => {
    if (it.kind === 'biblioteca') {
      onDraftChange({
        mode: 'biblioteca',
        id: it.f.id,
        name: it.f.nombre,
        imageUrl: it.f.imageUrl,
      });
    } else {
      onDraftChange({
        mode: 'guardado',
        fileName: it.f.fileName,
        name: it.f.nombre,
        imageUrl: it.f.imageUrl,
      });
    }
  };

  const autoSelected = draft?.mode === 'auto';
  const blancoSelected = draft?.mode === 'blanco';
  const descripcionSelected = draft?.mode === 'descripcion';
  const anadir = accionSecundaria === 'anadir';

  const addOptions = [
    {
      icon: <TypeIcon className="w-3.5 h-3.5" />,
      label: 'Describir tu fondo',
      onClick: onTapDescribe,
    },
    {
      icon: <UserPlus className="w-3.5 h-3.5" />,
      label: 'Cargá tu propio fondo',
      onClick: onTapUpload,
    },
  ];

  // Card de categoría (nivel 1): preview de fondo + label + cantidad +
  // chevron. Sin preview (Mis fondos vacío) el fondo queda brand-black.
  const categoryCard = (
    key: string,
    label: string,
    count: number,
    previewUrl: string,
    onClick: () => void,
  ) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className="relative w-full h-20 rounded-2xl overflow-hidden text-left shadow-sm active:scale-[0.98] transition-transform bg-brand-black"
    >
      {previewUrl && (
        <CachedImg
          src={previewUrl}
          alt={label}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-black/10" />
      <div className="relative z-10 h-full flex items-center justify-between px-4">
        <div className="min-w-0">
          <p className="text-white text-[15px] font-semibold leading-tight drop-shadow truncate">
            {label}
          </p>
          <p className="text-white/80 text-[12.5px] drop-shadow mt-0.5">
            {count} {count === 1 ? 'fondo' : 'fondos'}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-white/90 shrink-0" />
      </div>
    </button>
  );

  // ── Nivel 1: lista de categorías ─────────────────────────────────────
  const categoryList = (
    <div className="flex flex-col gap-2.5 md:gap-3">
      {/* Fila superior: Fondo automático + Describí tu fondo, lado a
          lado y un poco más altas que las cards de categoría. */}
      <div className="grid grid-cols-2 gap-2.5 md:gap-3">
        <button
          type="button"
          onClick={() => onDraftChange({ mode: 'auto' })}
          className={`relative h-24 rounded-2xl overflow-hidden text-white bg-brand-primary shadow-sm active:scale-[0.98] transition-transform ${
            autoSelected ? 'ring-2 ring-brand-primary ring-offset-2' : ''
          }`}
        >
          <AnimatedBlurBg pauseOnDesktopUnselected selected={autoSelected} />
          <div className="relative z-10 h-full flex flex-col items-start justify-between p-3">
            <Sparkles className="w-6 h-6 drop-shadow" />
            <span className="text-[14.5px] font-semibold drop-shadow text-left leading-tight">
              Fondo automático
            </span>
          </div>
          {autoSelected && (
            <span className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full inline-flex items-center justify-center bg-white text-brand-primary shadow-md">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          )}
        </button>
        {/* Segunda card: describir el fondo, o subir uno propio (catálogo).
            "Añadir fondo" no lleva estado seleccionado: lo que sube termina
            como un fondo guardado y se marca solo dentro de "Mis fondos". */}
        <button
          type="button"
          onClick={anadir ? onTapUpload : onTapDescribe}
          className={`relative h-24 rounded-2xl bg-white border-2 transition-colors active:scale-[0.98] ${
            !anadir && descripcionSelected
              ? 'border-brand-primary'
              : 'border-neutral-200 hover:border-[rgb(var(--brand-primary-rgb)_/_0.4)]'
          }`}
        >
          <div className="h-full flex flex-col items-start justify-between p-3">
            <span className="w-9 h-9 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.1)] inline-flex items-center justify-center">
              {anadir ? (
                <Upload className="w-4 h-4 text-brand-primary" />
              ) : (
                <TypeIcon className="w-4 h-4 text-brand-primary" />
              )}
            </span>
            <span className="text-[14.5px] font-semibold text-brand-black/85 text-left leading-tight">
              {anadir ? 'Añadir fondo' : 'Describí tu fondo'}
            </span>
          </div>
          {!anadir && descripcionSelected && (
            <span className="absolute top-2 right-2 w-6 h-6 rounded-full inline-flex items-center justify-center bg-brand-primary text-white shadow-md">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          )}
        </button>
      </div>
      {/* Fondo blanco */}
      <button
        type="button"
        // Re-tocar la opción no puede perder el tono/luz ya elegidos.
        onClick={() => onDraftChange(alElegirLiso(draft))}
        className={`relative w-full h-20 rounded-2xl bg-white border-2 text-left transition-colors active:scale-[0.98] ${
          blancoSelected
            ? 'border-brand-primary'
            : 'border-neutral-200 hover:border-[rgb(var(--brand-primary-rgb)_/_0.4)]'
        }`}
      >
        <div className="h-full flex items-center justify-between px-4">
          <span className="inline-flex items-center gap-2.5">
            <span
              className="inline-flex w-9 h-9 rounded-md border-2 border-neutral-300"
              style={{ backgroundColor: hexDelLiso(draft) }}
            />
            <span className="text-[15px] font-semibold text-brand-black/85">
              {etiquetaDelLiso(draft)}
            </span>
          </span>
          {blancoSelected && (
            <span className="w-6 h-6 rounded-full inline-flex items-center justify-center bg-brand-primary text-white shadow-md shrink-0">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          )}
        </div>
      </button>
      {ajustesBlanco}
      {/* Mis fondos — guardados del user + card añadir en el detalle. */}
      {categoryCard(
        MIS_FONDOS_CATEGORY,
        'Mis fondos',
        guardados.length,
        guardados[0]?.imageUrl || '',
        () => onCategoryChange(MIS_FONDOS_CATEGORY),
      )}
      {/* Categorías de biblioteca */}
      {categorias.map((c) =>
        categoryCard(c.id, c.id, c.count, c.previewUrl, () =>
          onCategoryChange(c.id),
        ),
      )}
    </div>
  );

  // ── Nivel 2: detalle de una categoría ────────────────────────────────
  // El "volver" y el título viven en el header del sheet (icon button +
  // nombre de la categoría), tanto en mobile como en desktop.
  const detail = category !== null && (
    <>
      {items.length === 0 && category !== MIS_FONDOS_CATEGORY ? (
        <p className="text-[13.5px] text-[#3C3C43]/55 mt-6 text-center">
          No hay fondos en esta categoría.
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3">
          {category === MIS_FONDOS_CATEGORY && (
            <AddOptionCard
              key="add-fondo"
              label="Añadir fondo"
              className="w-full min-h-[138px] md:min-h-[156px]"
              rounded="rounded-md md:rounded-[13px]"
              options={addOptions}
            />
          )}
          {items.map((it) => {
            const isSel = isItemSelected(it);
            return (
              <button
                key={`${it.kind}-${it.f.id}`}
                type="button"
                onClick={() => selectItem(it)}
                className={`relative rounded-md md:rounded-[13px] overflow-hidden bg-white border p-2 text-left transition-colors ${
                  isSel
                    ? 'border-brand-primary ring-2 ring-brand-primary'
                    : 'border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                <div className="relative overflow-hidden rounded-md h-24 md:h-28 bg-neutral-100">
                  {it.f.imageUrl && (
                    <CachedImg
                      src={it.f.imageUrl}
                      alt={it.f.nombre}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {isSel && (
                    <span className="absolute top-2 right-2 w-5 h-5 rounded-full inline-flex items-center justify-center bg-brand-primary text-white">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                  )}
                </div>
                <p className="text-[13px] text-neutral-700 mt-2 truncate">
                  {it.f.nombre}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 overflow-y-auto px-4 md:px-5 pt-2 md:pt-4 pb-4">
        {/* Keys distintas por nivel/categoría: el remount re-dispara la
            animación de swap (ida desde la derecha, vuelta desde la
            izquierda) al navegar entre lista y detalle. */}
        {category === null ? (
          <div key="categorias" className="animate-category-swap-back">
            {categoryList}
          </div>
        ) : (
          <div
            key={`detalle-${category}`}
            className="animate-category-swap"
          >
            {detail}
          </div>
        )}
      </div>
      {/* Footer Cancelar / Aceptar — solo desktop; en mobile el Aceptar
          vive en el header iOS del sheet (estilo PruebaNuevaInterfaz). */}
      <div className="shrink-0 px-5 py-3 hidden md:flex justify-end gap-3 border-t border-black/[0.06] bg-white">
        <button
          type="button"
          onClick={onCancel}
          className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => draft && onConfirm(draft)}
          disabled={!draft}
          className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
            draft
              ? 'text-white bg-brand-primary hover:bg-brand-primary-hover'
              : 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
          }`}
        >
          Aceptar
        </button>
      </div>
    </div>
  );
};

// ─── Sheet (mobile) ───────────────────────────────────────────────────

interface FondoPickerSheetProps {
  open: boolean;
  selection: FondoSelection | null;
  onChange: (sel: FondoSelection) => void;
  onClose: () => void;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  companyId: string | null;
  onFondoSaved: () => void;
  /** Vista inicial al abrir — útil para entrar directo a 'describe' o
   *  'upload' desde el inline picker en desktop. Default 'list'. */
  initialView?: FondoPickerView;
  /** Ver `AllFondosViewProps.accionSecundaria`. El catálogo manda 'anadir'. */
  accionSecundaria?: 'describir' | 'anadir';
  /** Línea bajo el título, en la vista RAÍZ del desktop (lista o biblioteca).
   *  Antes era un literal del catálogo hardcodeado en la vista 'list' — que
   *  aparecía también en los modos que no son catálogo. */
  subtitulo?: string;
  /** true → la Biblioteca muestra los ajustes de fondo blanco (tono + luz)
   *  debajo de esa opción. Lo usa el catálogo, donde el picker es el único
   *  lugar donde se define el fondo de toda la tanda. */
  mostrarAjustesBlanco?: boolean;
  /** Categoría inicial de la vista 'all' — para abrir el sheet directo
   *  en una categoría (cards del carrusel inline desktop). */
  initialCategory?: string | null;
  /** Nombres de los productos seleccionados — para el chat AI de
   *  "Describí el fondo" (mejorar idea / sugerir ideas). */
  selectedProductNames?: string[];
  /** Rubro de la empresa — también pasa al system prompt del agente. */
  rubro?: string | null;
}

export const FondoPickerSheet: React.FC<FondoPickerSheetProps> = ({
  open,
  selection,
  onChange,
  onClose,
  biblioteca,
  guardados,
  companyId,
  onFondoSaved,
  initialView = 'list',
  accionSecundaria = 'describir',
  subtitulo,
  mostrarAjustesBlanco = false,
  initialCategory = null,
  selectedProductNames = [],
  rubro = null,
}) => {
  const [view, setView] = useState<FondoPickerView>(initialView);
  // Vista raíz del sheet — a la que vuelve el "Volver" mobile. En mobile
  // es 'all' (menú de categorías estilo PruebaNuevaInterfaz); en desktop
  // 'list' salvo que se abra directo en 'all' (Ver más).
  const [rootView, setRootView] = useState<FondoPickerView>('list');
  // Draft de selección de AllFondosView — lifteado para que el Aceptar
  // del header mobile pueda commitearlo.
  const [allDraft, setAllDraft] = useState<FondoSelection | null>(selection);
  // Categoría activa dentro de AllFondosView (null = lista de
  // categorías) — lifteada para que el header mobile muestre Volver +
  // el nombre de la categoría.
  const [allCategory, setAllCategory] = useState<string | null>(null);
  // `useExitTransition` mantiene el sheet montado durante la animación
  // de salida cuando `open` pasa a false (al elegir un fondo o tocar
  // Cancelar / overlay) — así el sheet se desliza inverso antes de
  // desmontarse.
  const { mounted, animated } = useExitTransition(open, 320);
  // Texto del DescribeView lifteado para que el header (mobile) pueda
  // mostrar/disablar el botón "Usar" en su slot derecho.
  const [describeText, setDescribeText] = useState<string>(
    selection?.mode === 'descripcion' ? selection.text : '',
  );
  // Mobile chat-mode dentro de DescribeView. Cuando true, ocultamos el
  // "Usar" del header (el user usa "Utilizar" en cada sugerencia).
  const [describeChatMode, setDescribeChatMode] = useState(false);
  useEffect(() => {
    if (open) {
      // En mobile el carrusel 'list' ya no existe: la raíz es 'all'.
      const isMobile =
        typeof window !== 'undefined' &&
        !window.matchMedia('(min-width: 768px)').matches;
      const effective =
        initialView === 'list' && isMobile ? 'all' : initialView;
      setView(effective);
      setRootView(isMobile || effective === 'all' ? 'all' : 'list');
      setAllDraft(selection);
      setAllCategory(effective === 'all' ? initialCategory : null);
      setDescribeText(
        selection?.mode === 'descripcion' ? selection.text : '',
      );
      setDescribeChatMode(false);
      return;
    }
    // Reset interno del view con delay para no ver el wipe durante la
    // animación de salida.
    const t = setTimeout(() => setView('list'), 320);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Salir del chat-mode si el user cambia de view (volver a list, etc).
  useEffect(() => {
    if (view !== 'describe') setDescribeChatMode(false);
  }, [view]);

  if (!mounted) return null;

  const titleByView: Record<FondoPickerView, string> = {
    list: 'Fondo',
    describe: 'Describí el fondo',
    upload: 'Cargá tu fondo',
    all: 'Biblioteca de fondos',
  };
  // Desktop: título contextual + icon button de volver (estilo del botón
  // X) cuando hay adónde volver — dentro de una categoría el título es el
  // nombre de la categoría; en describe/upload se vuelve al menú de
  // fondos (conservando la categoría activa, ej. Mis fondos → Añadir).
  const desktopTitle =
    view === 'all' && allCategory
      ? fondoCategoryLabel(allCategory)
      : titleByView[view];
  const desktopBack: (() => void) | null =
    view === 'all' && allCategory
      ? () => setAllCategory(null)
      : view === 'describe' || view === 'upload'
        ? () => setView('all')
        : null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[630px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          // En mobile la altura del sheet (en className) ya excluye el
          // safe area top — bottom-anclado sin zona blanca arriba.
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Mobile: iOS-style header. */}
        <div className="md:hidden flex items-center justify-between px-4 pb-3 shrink-0">
          {view === 'all' && allCategory !== null ? (
            <button
              type="button"
              onClick={() => setAllCategory(null)}
              className="inline-flex items-center gap-0.5 text-brand-primary text-[15px] active:opacity-50 transition-opacity w-20 text-left"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          ) : view === rootView || view === 'list' ? (
            <button
              type="button"
              onClick={onClose}
              className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
            >
              Cancelar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setView(rootView)}
              className="inline-flex items-center gap-0.5 text-brand-primary text-[15px] active:opacity-50 transition-opacity w-20 text-left"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          )}
          <h3 className="text-[15px] font-semibold text-brand-black/90 truncate max-w-[55%] text-center">
            {view === 'all'
              ? allCategory
                ? fondoCategoryLabel(allCategory)
                : 'Fondo'
              : titleByView[view]}
          </h3>
          <div className="w-20 flex justify-end">
            {view === 'list' && (
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {view === 'all' && (
              <button
                type="button"
                onClick={() => {
                  if (!allDraft) return;
                  onChange(allDraft);
                  onClose();
                }}
                disabled={!allDraft}
                className="text-[15px] font-semibold text-brand-primary active:opacity-50 transition-opacity disabled:text-[rgb(var(--brand-primary-rgb)_/_0.3)] disabled:cursor-not-allowed"
              >
                Aceptar
              </button>
            )}
            {view === 'describe' && !describeChatMode && (
              <button
                type="button"
                onClick={() => {
                  const trimmed = describeText.trim();
                  if (!trimmed) return;
                  onChange({ mode: 'descripcion', text: trimmed });
                  onClose();
                }}
                disabled={describeText.trim().length === 0}
                className="text-[15px] font-semibold text-brand-primary active:opacity-50 transition-opacity disabled:text-brand-primary/30 disabled:cursor-not-allowed"
              >
                Utilizar
              </button>
            )}
          </div>
        </div>
        {/* Desktop: PruebaNuevaInterfaz-style (back icon opcional · title
            left · X right). */}
        <div className="hidden md:flex items-start justify-between px-5 py-4 shrink-0">
          <div className="min-w-0 flex items-center gap-3">
            {desktopBack && (
              <button
                type="button"
                onClick={desktopBack}
                className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                aria-label="Volver"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div className="min-w-0">
              <h3 className="text-[17px] font-semibold text-brand-black/90 truncate">
                {desktopTitle}
              </h3>
              {subtitulo &&
                (view === 'list' || (view === 'all' && allCategory === null)) && (
                  <p className="text-[13px] text-[#3C3C43]/55 mt-0.5">
                    {subtitulo}
                  </p>
                )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {view === 'list' ? (
          <div className="flex-1 min-h-0 px-5 pt-2 pb-6">
            <ListView
              selection={selection}
              biblioteca={biblioteca}
              guardados={guardados}
              variant="sheet"
              onSelectAuto={() => {
                onChange({ mode: 'auto' });
                onClose();
              }}
              onSelectBlanco={() => {
                onChange({ mode: 'blanco' });
                onClose();
              }}
              onSelectBiblioteca={(f) => {
                onChange({
                  mode: 'biblioteca',
                  id: f.id,
                  name: f.nombre,
                  imageUrl: f.imageUrl,
                });
                onClose();
              }}
              onSelectGuardado={(f) => {
                onChange({
                  mode: 'guardado',
                  fileName: f.fileName,
                  name: f.nombre,
                  imageUrl: f.imageUrl,
                });
                onClose();
              }}
              onTapDescribe={() => setView('describe')}
              onTapUpload={() => setView('upload')}
              accionSecundaria={accionSecundaria}
            />
          </div>
        ) : view === 'all' ? (
          <AllFondosView
            draft={allDraft}
            onDraftChange={setAllDraft}
            category={allCategory}
            onCategoryChange={setAllCategory}
            biblioteca={biblioteca}
            guardados={guardados}
            onConfirm={(sel) => {
              onChange(sel);
              onClose();
            }}
            onCancel={onClose}
            onTapDescribe={() => setView('describe')}
            onTapUpload={() => setView('upload')}
            accionSecundaria={accionSecundaria}
            ajustesBlanco={
              mostrarAjustesBlanco ? (
                // Atados al DRAFT, no a la selección: acá se confirma con
                // "Aceptar", así que tocar un chip no debe commitear solo.
                <AjustesFondoBlanco
                  selection={allDraft}
                  onChange={setAllDraft}
                  className="bg-[#F7F7F9] rounded-2xl p-2"
                />
              ) : null
            }
          />
        ) : (
          <div className="flex-1 overflow-y-auto px-5 pt-2 pb-6 md:pt-4 md:pb-0">
            {view === 'describe' && (
              <DescribeView
                initial={
                  selection?.mode === 'descripcion' ? selection.text : ''
                }
                onBack={onClose}
                onConfirm={(text) => {
                  onChange({ mode: 'descripcion', text });
                  onClose();
                }}
                onTextChange={setDescribeText}
                selectedProductNames={selectedProductNames}
                rubro={rubro}
                chatMode={describeChatMode}
                onChatModeChange={setDescribeChatMode}
              />
            )}
            {view === 'upload' && (
              <FondoUploadView
                companyId={companyId}
                onFondoSaved={onFondoSaved}
                onConfirm={(saved) => {
                  onChange({
                    mode: 'guardado',
                    fileName: saved.fileName,
                    name: saved.nombre,
                    imageUrl: saved.imageUrl,
                  });
                  onClose();
                }}
              />
            )}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};
